import {colorSwatch} from '../utils/colorSwatch.js';
import {MenuPanels} from '../../models/UIModel.js';
import React, {useState, useEffect, useMemo} from 'react';
import {MenuButton} from '../components/menu-button.js';

import {
    Column,
    Container,
    Image,
    Panel,
    Panel9Slice,
    PanelProps,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {Align, FlexDirection, Justify, PositionType} from '@wonderlandengine/react-ui';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {Window} from '../components/window.js';
import {WindowHeader} from '../components/window-header.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {goToGame} from '../utils/goToGame.js';
import {Carousel, CarouselItem} from '../components/carousel.js';
import {SliderItem} from '../components/SliderComponent.js';
import {LeaderboardInventoryItems} from '../../enums/LeaderboardItems.js';
import {Texture} from '@wonderlandengine/api';
import {UIColorTextured} from '../../types/materials.js';
import {formatString, parseColor} from '../utils/utils.js';
import {LeaderboardManager} from '../../components/leaderboard-manager.js';
import {GameModeType} from '../../enums/GameModeType.js';
import {user} from '@wonderlandengine/uber-sdk';
import {Localization} from '../../utils/Localization.js';
import {GameFlowController} from '../../components/game-flow-controller.js';
import {Sounds} from '../../enums/Sounds.js';
import {goToPreviousMenu} from '../utils/goToPreviousMenu.js';
import {clipString, formatNumber} from '../../utils/Utils.js';

const LIST_MARGIN = 10;

const getPositionTexture = (position) => {
    if (position == 1) {
        return UiTexturesManager.instance.leaderBoardGold;
    } else if (position == 2) {
        return UiTexturesManager.instance.leaderBoardSilver;
    } else if (position == 3) {
        return UiTexturesManager.instance.leaderBoardBronze;
    } else {
        return UiTexturesManager.instance.leaderBoardEmpty;
    }
};

//TO DO : update string based on reset logic
const expireString = '0 days 0 hours 0 minutes 0 seconds';

function isLeaderboardItemLocked(storeItem: LeaderboardInventoryItems): boolean {
    return false;
    //TO DO : add logic for "if the mode is locked" once we implement multiple modes
}

function leaderboardCountdown() {
    // Constants matching the parent's logic:
    // FIRST_TERM_START: Oct 2, 2012 @ 1:00 AM PST (in seconds)
    const FIRST_TERM_START = 1349164800;
    // TERM_LENGTH: 1 week (in seconds)
    const TERM_LENGTH = 60 * 60 * 24 * 7;

    // Get the current time in seconds since the epoch.
    const currentTime = Math.floor(Date.now() / 1000);

    // Calculate the current term start.
    const delta = currentTime - FIRST_TERM_START;
    const numberOfTerms = Math.floor(delta / TERM_LENGTH);
    const currentTermStart = FIRST_TERM_START + numberOfTerms * TERM_LENGTH;

    // Determine when the current term ends and the time remaining.
    const termEnd = currentTermStart + TERM_LENGTH;
    const timeRemaining = termEnd - currentTime;

    // Break the remaining time into days, hours, and minutes.
    const days = Math.floor(timeRemaining / (60 * 60 * 24));
    const hours = Math.floor((timeRemaining % (60 * 60 * 24)) / (60 * 60));
    const minutes = Math.floor((timeRemaining % (60 * 60)) / 60);

    // Retrieve the localization strings.
    const format = Localization.instance.get('LeaderboardCountdown');
    const daySingular = Localization.instance.get('DaySingular');
    const dayPlural = Localization.instance.get('DayPlural');
    const hourSingular = Localization.instance.get('HourSingular');
    const hourPlural = Localization.instance.get('HourPlural');
    const minuteSingular = Localization.instance.get('MinuteSingular');
    const minutePlural = Localization.instance.get('MinutePlural');

    // Choose the correct singular/plural based on the count.
    const dayText =
        days === 1 ? formatString(daySingular, days) : formatString(dayPlural, days);
    const hourText =
        hours === 1 ? formatString(hourSingular, hours) : formatString(hourPlural, hours);
    const minuteText =
        minutes === 1
            ? formatString(minuteSingular, minutes)
            : formatString(minutePlural, minutes);

    // Replace placeholders {0}, {1}, and {2} with the calculated values.
    const countdownString = formatString(format, dayText, hourText, minuteText);

    return countdownString;
}

const PositionIcon = (props: {position?: number}) => {
    return (
        <>
            {props.position < 4 ? (
                <Image
                    // margin={5}
                    width={30} // Reduced size
                    height={30}
                    rounding={0}
                    src={getPositionTexture(props.position)}
                ></Image>
            ) : (
                <Panel
                    rounding={15}
                    backgroundColor="#3e444e"
                    borderColor="#aaaaaa"
                    borderSize={2}
                    // margin={5}
                    minWidth={30} // Reduced size
                    height={30}
                    justifyContent={Justify.Center}
                    alignContent={Align.Center}
                    alignItems={Align.Center}
                    paddingLeft={5}
                    paddingRight={5}
                >
                    <Text
                        minWidth={20}
                        textAlign="center"
                        fontSize={props.position < 100 ? 15 : 10}
                        text={props.position.toString()}
                    ></Text>
                </Panel>
            )}
        </>
    );
};

export const LeaderboardLine = (props: {item: highScore}) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;

    function useMatGrad(mat, color) {
        return useMemo(() => {
            const bm = mat.clone();
            (bm as UIColorTextured).setColor(parseColor(color));
            return bm;
        }, [mat, color]);
    }
    const listMaterial = useMatGrad(rotbMats.whiteBevelMaterial, '#666f7f');
    const listMaterialHighlighted = useMatGrad(rotbMats.whiteBevelMaterial, '#4e8a9f');

    const getListMat = (item: highScore) => {
        if (item.isCurrentUser) {
            return listMaterialHighlighted; // user rank
        }
        return listMaterial;
    };

    return (
        <Panel9Slice
            rounding={0}
            borderSize={5}
            borderTextureSize={9 / 64}
            material={getListMat(props.item)}
            flexDirection={FlexDirection.Row}
            justifyContent={Justify.FlexStart}
            alignContent={Align.Center}
            alignItems={Align.Center}
            padding={10}
            gap={5}
            width="100%"
        >
            <PositionIcon position={props.item.rank}></PositionIcon>
            <Text
                flexShrink={1}
                textWrap="clip"
                fontSize={20}
                textAlign="left"
                marginRight={15}
                material={rotbMats.regularTextMaterial}
            >
                {clipString(props.item.username, 22)}
            </Text>
            <Text flexGrow={1} fontSize={20} textAlign="right">
                {formatNumber(props.item.score)}
            </Text>
        </Panel9Slice>
    );
};

type highScore = {
    username: string;
    score: number;
    rank: number;
    isCurrentUser: boolean;
    isGap: boolean;
};

/**
 * Creates a high score list for the leaderboard.
 *
 * If a user has a rank:
 * - Either the user is in the top 6 so it's just the top 6
 * - if the user is outside the top 6, the list shows the top 3, a gap, the score above the user and the user
 * if the user has no rank, just show the top 6.
 * @param gameMode - The game mode for which to create the leaderboard list.
 */
function createLeaderboardList(gameMode: GameModeType): highScore[] {
    const currentUserRank = LeaderboardManager.instance.getUserRank(gameMode);
    let highScores: highScore[] = [];
    const listSize = 6;

    if (currentUserRank <= 6 && currentUserRank !== -1) {
        // User is in the top 6, show the top 6
        for (let i = 0; i < listSize; i++) {
            const username = LeaderboardManager.instance.getUserName(gameMode, i + 1);
            const score = LeaderboardManager.instance.getScore(gameMode, i + 1);
            const isCurrentUser = currentUserRank === i + 1;
            highScores.push({
                username: username || '',
                score: score || 0,
                rank: i + 1,
                isCurrentUser,
                isGap: false,
            });
        }
    } else if (currentUserRank > 6) {
        // User is outside the top 6, show top 3, gap, user's rank - 1, and user's rank
        // Top 3
        for (let i = 0; i < 3; i++) {
            const username = LeaderboardManager.instance.getUserName(gameMode, i + 1);
            const score = LeaderboardManager.instance.getScore(gameMode, i + 1);
            const isCurrentUser = false;
            highScores.push({
                username: username,
                score: score || 0,
                rank: i + 1,
                isCurrentUser,
                isGap: false,
            });
        }

        // Gap
        highScores.push({
            username: '',
            score: 0,
            rank: 0,
            isCurrentUser: false,
            isGap: true,
        });

        const rankBeforeUser = currentUserRank - 1;

        // Rank before user
        if (rankBeforeUser > 0) {
            const usernameBefore = LeaderboardManager.instance.getUserName(
                gameMode,
                rankBeforeUser
            );
            const scoreBefore = LeaderboardManager.instance.getScore(
                gameMode,
                rankBeforeUser
            );
            highScores.push({
                username: usernameBefore,
                score: scoreBefore || 0,
                rank: rankBeforeUser,
                isCurrentUser: false,
                isGap: false,
            });
        }

        // User's rank
        const username =
            user?.user?.name || Localization.instance.get('AnonymousLocalUserName');
        const score = LeaderboardManager.instance.getScore(gameMode, currentUserRank);
        highScores.push({
            username: username,
            score: score || 0,
            rank: currentUserRank,
            isCurrentUser: true,
            isGap: false,
        });

        // Fill the rest of the list with empty entries if needed
        while (highScores.length < listSize) {
            highScores.push({
                username: '',
                score: 0,
                rank: 0,
                isCurrentUser: false,
                isGap: false,
            });
        }
    } else {
        // No user rank, show the top 6
        for (let i = 0; i < listSize; i++) {
            const username = LeaderboardManager.instance.getUserName(gameMode, i + 1);
            const score = LeaderboardManager.instance.getScore(gameMode, i + 1);
            const isCurrentUser = false;
            highScores.push({
                username: username || '',
                score: score || 0,
                rank: i + 1,
                isCurrentUser,
                isGap: false,
            });
        }
    }

    return highScores;
}

export const Leaderboard = (props: React.PropsWithChildren<PanelProps> & {}) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;

    function useMatGrad(mat, color) {
        return useMemo(() => {
            const bm = mat.clone();
            (bm as UIColorTextured).setColor(parseColor(color));
            return bm;
        }, [mat, color]);
    }

    const subTextMaterial = useMatGrad(
        rotbMats.regularTextMaterial,
        colorSwatch.LeaderboardSubText
    );
    const outerPanelMaterial = useMatGrad(rotbMats.titleBackgroundMaterial, '#385576');
    const innerPanelMaterial = useMatGrad(rotbMats.titleBackgroundMaterial, '#2d4761');

    const transparentMaterial = rotbMats.uiTextShadowMaterial;

    function lockImage(storeItem: LeaderboardInventoryItems, image: Texture): Texture {
        if (!isLeaderboardItemLocked(storeItem)) {
            return image;
        } else return UiTexturesManager.instance.buttonLock;
    }

    const onSelectionChanged = (item: SliderItem) => {
        const gameMode = GameModeType[item.id];
        setGameMode(gameMode);
        setGameModeText(Localization.instance.get(GameModeType[gameMode]));
        setHighScores(createLeaderboardList(gameMode));
    };

    const [highScores, setHighScores] = useState<highScore[]>([]);

    const [isLoggedIn, setIsLoggedIn] = useState(user.isLoggedIn);

    //useEffect(() => {
    // const getIsLoggedIn = async () => {
    //     await user.requestLogin();
    //     setIsLoggedIn(user.isLoggedIn);
    // };
    // if (!isLoggedIn) {
    //     getIsLoggedIn();
    // }
    //}, []);

    const [gameMode, setGameMode] = useState(GameModeType.Quickplay);
    const [gameModeText, setGameModeText] = useState('Star');

    const [countdown] = useState(leaderboardCountdown());
    const handleClick = ({e}) => {
        GameFlowController.instance.setMode(gameMode);
        goToGame(e);
    };

    return (
        <Container
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Row}
            justifyContent={Justify.Center}
        >
            <Column marginTop={36}>
                <Window>
                    <Column
                        height="100%"
                        marginTop={20}
                        alignSelf={Align.Center}
                        width={500}
                        rowGap={10}
                    >
                        <WindowHeader title={Localization.instance.get('Leaderboard')} />
                        {/* <Text textAlign="center" fontSize={17} material={subTextMaterial}>
                            {countdown}
                        </Text> */}
                        <Carousel
                            marginTop={10}
                            height={200}
                            loop={false}
                            onSelectionChanged={onSelectionChanged}
                        >
                            <CarouselItem
                                title={Localization.instance.get(
                                    GameModeType[GameModeType.Quickplay]
                                )}
                                id={GameModeType[GameModeType.Quickplay]}
                                image={UiTexturesManager.instance.leaderBoardStar}
                            ></CarouselItem>

                            <CarouselItem
                                title={Localization.instance.get(
                                    GameModeType[GameModeType.Easy]
                                )}
                                id={GameModeType[GameModeType.Easy]}
                                image={lockImage(
                                    LeaderboardInventoryItems.normal,
                                    UiTexturesManager.instance.leaderBoardNormal
                                )}
                            ></CarouselItem>
                        </Carousel>

                        <Text alignSelf={Align.Center} fontSize={30} textAlign="center">
                            {gameModeText}
                        </Text>
                        <Panel
                            width="100%"
                            rounding={0}
                            material={outerPanelMaterial}
                            alignContent={Align.Center}
                            justifyContent={Justify.Center}
                        >
                            <Panel margin={10} rounding={0} material={innerPanelMaterial}>
                                <Column
                                    width="100%"
                                    justifyContent={Justify.SpaceBetween} // Spreads rows evenly
                                    alignItems={Align.Stretch} // Ensures rows stretch to full width
                                    gap={5}
                                >
                                    {highScores.map((item, index) =>
                                        !item.isGap ? (
                                            <LeaderboardLine
                                                key={index}
                                                item={item}
                                            ></LeaderboardLine>
                                        ) : (
                                            <Container
                                                margin={10}
                                                width="100%"
                                                justifyContent={Justify.Center}
                                                alignItems={Align.Center}
                                                key={index}
                                            >
                                                <Column marginRight={5} gap={5}>
                                                    <Panel
                                                        rounding={5}
                                                        width={5}
                                                        height={5}
                                                    ></Panel>
                                                    <Panel
                                                        rounding={5}
                                                        width={5}
                                                        height={5}
                                                    ></Panel>
                                                    <Panel
                                                        rounding={5}
                                                        width={5}
                                                        height={5}
                                                    ></Panel>
                                                </Column>
                                            </Container>
                                        )
                                    )}
                                </Column>
                            </Panel>
                        </Panel>

                        <Container flexGrow={1} marginTop={10}>
                            {!isLoggedIn && (
                                <Text
                                    fontSize={12}
                                    textAlign="center"
                                    text={Localization.instance.get('TutorialMenus3')}
                                ></Text>
                            )}
                        </Container>
                    </Column>
                </Window>
                <Row justifyContent={Justify.Center}>
                    <MenuButton
                        width={250}
                        height={54}
                        margin={6}
                        color={colorSwatch.CancelButton}
                        fontSize={32}
                        icon={UiTexturesManager.instance.arrow}
                        rotation={180}
                        iconWidth={38}
                        iconHeight={40}
                        sound={Sounds.cancel}
                        onClick={() => goToPreviousMenu(MenuPanels.Leaderboard)}
                    >
                        {Localization.instance.get('LoseProgressDialogReject')}
                    </MenuButton>
                    <MenuButton
                        width={250}
                        height={54}
                        margin={6}
                        color={colorSwatch.AcceptButton}
                        fontSize={32}
                        iconWidth={38}
                        iconHeight={40}
                        onClick={handleClick}
                        animate={true}
                        icon={UiTexturesManager.instance.arrow}
                    >
                        {Localization.instance.get('Play')}
                    </MenuButton>
                </Row>
            </Column>
        </Container>
    );
};
